import * as React from 'react'
import { classNames } from './helpers'

export interface DurationProps {
  /**
   * The length to show — either a total number of seconds, or a colon timecode
   * string the way the catalog sends it (`HH:MM:SS`, `MM:SS`, or a bare count of
   * seconds). Anything unparseable renders nothing, so a caller can pass a raw
   * field straight through without guarding it first.
   */
  value: number | string | null | undefined;
  className?: string;
}

interface Parts { hours: number, minutes: number, seconds: number }

/** A colon timecode (`52:52`, `1:20:05`) or a bare number of seconds → total seconds, or null. */
function toSeconds (value: number | string | null | undefined): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 ? Math.round(value) : null
  }
  const trimmed = value?.trim()
  if (!trimmed) return null
  // `12:34:56` → [h, m, s], `12:34` → [m, s], `90` → [s]. Any non-digit segment
  // (or more than three) means it isn't a timecode we understand — bail to null.
  const segments = trimmed.split(':')
  if (segments.length > 3) return null
  let total = 0
  for (const segment of segments) {
    if (!/^\d+$/.test(segment)) return null
    total = total * 60 + Number(segment)
  }
  return total
}

function split (total: number): Parts {
  return {
    hours: Math.floor(total / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  }
}

/**
 * `52:52`, `1:31`, `1:20:05` — the visible clock. Hours (and their leading zero)
 * drop when there are none, so a 90-second clip reads as a length rather than as
 * the timecode `00:01:30`. Seconds keep their padding so `1:05` can't be misread
 * as `1:50`.
 */
function toClock ({ hours, minutes, seconds }: Parts): string {
  const ss = String(seconds).padStart(2, '0')
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${ss}`
    : `${minutes}:${ss}`
}

/** ISO 8601 duration for the machine-readable `datetime` — `PT52M52S`, `PT1H20M5S`. */
function toIso ({ hours, minutes, seconds }: Parts): string {
  const body = `${hours ? `${hours}H` : ''}${minutes ? `${minutes}M` : ''}${seconds ? `${seconds}S` : ''}`
  return `PT${body || '0S'}`
}

/**
 * The spoken form for assistive tech — "52 minutes 52 seconds", "1 hour 20
 * minutes 5 seconds". A screen reader reads the ambiguous "52:52" as digits and a
 * colon; this says the units out loud instead. Zero components are dropped, but a
 * zero total still says "0 seconds" rather than nothing.
 */
function toSpoken ({ hours, minutes, seconds }: Parts): string {
  const unit = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`
  const words: string[] = []
  if (hours) words.push(unit(hours, 'hour'))
  if (minutes) words.push(unit(minutes, 'minute'))
  if (seconds || words.length === 0) words.push(unit(seconds, 'second'))
  return words.join(' ')
}

/**
 * A length of time — the "52:52" beside a stream, a clip's runtime, and so on.
 *
 * Renders a semantic `<time>`: its `dateTime` carries the machine-readable ISO
 * 8601 duration, and its `aria-label` spells the length out in words so a screen
 * reader never has to read the ambiguous clock digits aloud. Figures are tabular
 * (see the stylesheet) so a stack of durations lines up on the colon.
 */
export function Duration ({ value, className }: DurationProps) {
  const total = toSeconds(value)
  if (total === null) return null
  const parts = split(total)
  return (
    <time
      className={classNames('mlsg-duration', className)}
      dateTime={toIso(parts)}
      aria-label={toSpoken(parts)}
    >
      {toClock(parts)}
    </time>
  )
}
